import {
	FormControl,
	FormControlProps,
	FormHelperText,
	TextField,
	TextFieldProps,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';
import { isNumeric } from '../../lib/helpers/types';

// Frozen variables for controlling key presses
const ControlKeys = [ 'Control', 'Meta' ] as const;
const ControlKeyModifiers = [
	'c',
	'v',
	'x'
] as const;
const ManipulationKeys = [
	'Tab',
	'Enter',
	'Backspace',
	'Delete',
	'ArrowLeft',
	'ArrowRight',
	'Home',
	'End',
	'Shift',
] as const;

// make it easier to change the current to eventually support canada or australia
const decimalCharacter = '.';

type MuiNumberInputPropsOnChange = ( value: number ) => void;
type MuiNumberInputProps = {
	errorText?: string;
	value?: number;
	onChange?: MuiNumberInputPropsOnChange; // custom onChange to handle type=number value
	min?: number;
	max?: number;
	limit?: RegExp;
	onBlur?: MuiNumberInputPropsOnChange;
} & Omit<
	TextFieldProps,
	'value' | 'onChange' | 'variant' | 'error' | 'variant' | 'onBlur'
> &
	Pick<FormControlProps, 'variant'>;

const MuiNumberInput: React.FC<MuiNumberInputProps> = ( props ) => {
	const {
		className,
		fullWidth,
		errorText,
		variant,
		min,
		max,
		limit,
		onBlur,
		...propsToSpread
	} = props;
	const ref = useErrorScrollContext( !!errorText );
	const [ displayAmount, setDisplayAmount ] = useState( '' );
	// so we detect when the control key is down to allow ctrl+x but disallow just x, without re-rendering
	const controlKeyDownRef = useRef( false );
	const displayValueHasDecimal = displayAmount.indexOf( decimalCharacter ) > 0;
	const convertDisplayAmountAndFormat = useCallback(
		( value?: string ) => {
			const amountToUse = value || displayAmount;
			// only allow one period, and assume dollars not pennies is the intent
			const [ left, ...rightPart ] =
				amountToUse.indexOf( decimalCharacter ) > -1
					? amountToUse.split( decimalCharacter )
					: [ amountToUse, '0' ];
			// this way if the user enters '12.34.56' that gets converted into dollars=12 and cents=3456
			const right = rightPart.join();
			// convert to a dollar amount
			const formattedAmount = `${ left }${ left ? decimalCharacter : '' }${ right }`;
			const numberValue = parseFloat( formattedAmount );
			return { numberValue, formattedAmount };
		},
		[ displayAmount ]
	);
	// Format the value
	const handleOnBlur = useCallback( () => {
		const { numberValue, formattedAmount } = convertDisplayAmountAndFormat();
		setDisplayAmount( formattedAmount );
		props.onBlur?.( numberValue );
	}, [ convertDisplayAmountAndFormat, props ] );
	const handleKeyDown = useCallback(
		( e: React.KeyboardEvent<HTMLDivElement> ) => {
			const isPercentageKey = new RegExp( `[\\d${ decimalCharacter }]` ).test(
				e.key
			);
			const isAllowedNonPercentageKey = ( [] as string[] )
				.concat( ManipulationKeys )
				.concat( ...ControlKeys )
				.concat( controlKeyDownRef.current ? ControlKeyModifiers : [] )
				.includes( e.key );
			// Also, don't allow more than 1 period in the value
			if ( ( ControlKeys as unknown as string[] ).includes( e.key ) ) {
				controlKeyDownRef.current = true;
			}
			const isNthPeriod = e.key === decimalCharacter && displayValueHasDecimal;
			if ( ( !isPercentageKey && !isAllowedNonPercentageKey ) || isNthPeriod ) {
				e.preventDefault();
			}
			if ( e.key === 'Enter' ) {
				/* Clicking on buttons causes blur to fire here before submit on the
				parent, but not the enter key. So in order to get the right currency
				value ensure onBlur fires before onSubmit */
				handleOnBlur();
			}
		},
		[ displayValueHasDecimal, handleOnBlur ]
	);
	const handleKeyUp = useCallback( ( e: React.KeyboardEvent<HTMLDivElement> ) => {
		const isLiterallyControlKey = ( [] as string[] )
			.concat( ControlKeys )
			.includes( e.key );
		if ( isLiterallyControlKey ) {
			controlKeyDownRef.current = false;
		}
	}, [] );
	const isBelowMax = useCallback(
		( value: string ) =>
			( typeof max === 'number' && parseFloat( value ) <= max ) ||
			typeof max == 'undefined',
		[ max ]
	);
	const isAboveMin = useCallback(
		( value: string ) =>
			( typeof min === 'number' && parseFloat( value ) >= min ) ||
			typeof min == 'undefined',
		[ min ]
	);
	const isWithinLimit = useCallback(
		( value: string ) =>
			( limit && limit.test( value ) ) || typeof limit === 'undefined',
		[ limit ]
	);
	const isValidValue = useCallback(
		( value: string ) =>
			value === ''
				? true
				: isBelowMax( value ) && isAboveMin( value ) && isWithinLimit( value ),
		[
			isAboveMin,
			isBelowMax,
			isWithinLimit
		]
	);
	const handleOnChange = useCallback(
		( e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> ) => {
			const sanitizedValue = e.target.value?.replace(
				new RegExp( `[${ decimalCharacter }]+`, 'g' ),
				decimalCharacter
			);
			if ( isValidValue( sanitizedValue ) ) {
				setDisplayAmount( sanitizedValue );
				const { numberValue } = convertDisplayAmountAndFormat( sanitizedValue );
				props.onChange?.( numberValue );
			}
		},
		[
			convertDisplayAmountAndFormat,
			isValidValue,
			props
		]
	);
	// Make sure when the prop changes this value changes - but if it's not a number, do nothing
	useEffect( () => {
		if ( isNumeric( props.value ) ) {
			// assuming input of pennies
			const numberValue = parseFloat( props.value as unknown as string );
			const formattedAmount = numberValue.toString();
			setDisplayAmount( formattedAmount );
		}
	}, [ props.value, setDisplayAmount ] );
	return (
		<FormControl
			variant={ variant }
			error={ !!props.errorText }
			fullWidth={ fullWidth }
			className={ className }
		>
			<TextField
				{ ...propsToSpread }
				fullWidth={ fullWidth }
				value={ displayAmount === `0${ decimalCharacter }00` ? '' : displayAmount }
				// allow numbers, periods, commas, and some control-keys
				onKeyDown={ handleKeyDown }
				onKeyUp={ handleKeyUp }
				onChange={ handleOnChange }
				onBlur={ handleOnBlur }
				slotProps={ {
					...propsToSpread.slotProps,
					htmlInput: {
						...propsToSpread.slotProps?.htmlInput,
						/* this is better than type=number in that it counts "12."" as a valid value.
							Also, decimal is better than numeric because on iOS this includes numbers AND a period in the keyboard */
						inputMode: 'decimal',
					},
				} }
				error={ !!props.errorText }
				ref={ ref }
			/>
			{ props.errorText ? (
				<FormHelperText>{ props.errorText }</FormHelperText>
			) : null }
		</FormControl>
	);
};

export default MuiNumberInput;
